/* eslint-disable react/forbid-prop-types */

import i18n from "lib/i18n";
import PropTypes from "prop-types";
import React, { useContext, useState } from "react";
import {
  Checkbox,
  Dialog as UIDialog,
  DialogAction,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
  withStyles
} from "@theconversation/ui";

import RemovalReasons from "../../RemoveComment/RemovalReasons";
import FeatureFlipsContext from "../../FeatureFlipsContext";

const styles = theme => ({
  title: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  field: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
});

/**
 * React component that'll render the comment removal reason dialog, which can be closed or confirmed.
 * When a user confirms the removal reason, this component is responsible to submit the reason to the server.
 *
 * @param {Object} classes injected JSS styles
 * @param {Function} closeModal function to handle closing the dialog
 * @param {string} dialogTitle optional title for the dialog
 * @param {Function} onSubmitDialog function to be called when dialog is submitted
 * @param {Boolean} open defines if the dialog is opened
 * @param {String} userName user full name
 */
function Dialog({
  classes,
  closeModal,
  dialogTitle,
  onSubmitDialog,
  open,
  userName,
}) {
  const [removalReason, setRemovalReason] = useState();
  const [notes, setNotes] = useState();
  const [requireModeration, setRequireModeration] = useState(false);
  const { manageUser: manageUserFeatureFlip } = useContext(FeatureFlipsContext);

  const handleDialogSubmit = (event) => {
    event.preventDefault();

    onSubmitDialog({ removalReason, requireModeration, notes });
  };

  return (
    <UIDialog
      onClose={closeModal}
      open={open}
    >
      <DialogContent>
        <Grid container>
          <Grid item xs={12} className={classes.title}>
            <Typography variant="h3">
              {dialogTitle || i18n.t("comment.messages.remove_comment")}
            </Typography>
          </Grid>

          <Grid item xs={12} className={classes.field}>
            <RemovalReasons setRemovalReason={setRemovalReason} />
          </Grid>

          <Grid item xs={12} className={classes.field}>
            <TextField
              autoFocus
              fullWidth
              label={i18n.t("comment.label.notes")}
              helperText={i18n.t("comment.label.notes_hint")}
              multiline
              name="notes"
              onChange={event => setNotes(event.target.value)}
            />
          </Grid>

          {
            manageUserFeatureFlip ?
              <React.Fragment>
                <Grid item xs={12}>
                  <Typography variant="button">
                    {i18n.t("editorial_comments_new.label.manage_user", { name: userName })}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        onChange={event => setRequireModeration(event.target.checked)}
                        value="checkedC"
                      />
                    }
                    label={
                      <Typography variant="subtitle1">
                        {i18n.t("editorial_comments_new.label.require_moderation_on_future_comments")}
                      </Typography>
                    }
                  />
                </Grid>
              </React.Fragment> :
              null
          }
        </Grid>
      </DialogContent>

      <DialogActions>
        <DialogAction onClick={closeModal}>
          {i18n.t("comment.button.cancel")}
        </DialogAction>

        <DialogAction onClick={handleDialogSubmit} variant="primary">
          {i18n.t("comment.button.confirm")}
        </DialogAction>
      </DialogActions>
    </UIDialog>
  );
}

Dialog.defaultProps = {
  classes: {},
  dialogTitle: null,
};

Dialog.propTypes = {
  classes: PropTypes.object,
  closeModal: PropTypes.func.isRequired,
  dialogTitle: PropTypes.string,
  onSubmitDialog: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  userName: PropTypes.string.isRequired,
};

export default withStyles(styles)(Dialog);
